/*
 * DebugInfoPanel renders DebugInfoCollector sections as a tabbed overlay panel.
 *
 * The panel is independent from Leaflet controls and periodically refreshes
 * visible statCollector sections mounted inside the 3D viewer dialog.
 */
let panelCounter = 0;

export class DebugInfoPanel {
    /**
     * Creates a tabbed debug panel around a DebugInfoCollector instance.
     *
     * @param {DebugInfoCollector} collector Collector that owns stat sections and values.
     * @param {{refreshInterval?: number}} [options] Panel display options.
     * @param {number} [options.refreshInterval=1000] Automatic refresh interval in milliseconds.
     * @returns {DebugInfoPanel} Initialized panel controller.
     */
    constructor(collector, options = {}) {
        this.collector = collector;
        this.refreshInterval = options.refreshInterval || 1000;
        this.container = null;
        this.idPrefix = `threeD-debug-${++panelCounter}`;
        this.timerId = null;
        this.activeSectionId = collector.getSections()[0]?.id || null;
        this.cameraInput = null;
        this.cameraStatus = null;
    }

    /**
     * Mounts the panel into a DOM container and binds collector display nodes.
     *
     * @param {HTMLElement} container Container that receives tab markup.
     * @returns {void}
     * @throws {Error} Throws when the container is missing.
     */
    mount(container) {
        if (!container) {
            throw new Error("DebugInfoPanel container is required");
        }

        this.container = container;
        this.container.innerHTML = this._createContent();
        this._bindTabs();
        this._bindCameraControls();
        this._bindStatContainers();
        this.refresh();
        this.timerId = setInterval(() => this.refresh(), this.refreshInterval);
    }

    /**
     * Re-renders currently visible debug values.
     *
     * @returns {void}
     */
    refresh() {
        this.collector.getSections()
            .filter((section) => section.id !== "camera")
            .forEach((section) => section.stat.show());
        this._refreshCameraSection();
    }

    /**
     * Stops automatic refresh and clears panel DOM references.
     *
     * @returns {void}
     */
    destroy() {
        if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = null;
        }

        this.cameraInput = null;
        this.cameraStatus = null;
        this.container = null;
    }

    /**
     * Creates the static tab markup for all collector sections.
     *
     * @returns {string} HTML string with tab buttons and section containers.
     */
    _createContent() {
        const sections = this.collector.getSections();
        const tabs = sections.map((section) => {
            const activeClass = section.id === this.activeSectionId ? " selectedItem" : "";

            return `<li><button type="button" class="threeDDebugPanel__tab${activeClass}" data-debug-tab="${section.id}">${section.title}</button></li>`;
        }).join("");
        const content = sections.map((section) => {
            const activeClass = section.id === this.activeSectionId ? " selectedItem" : "";
            const sectionContent = section.id === "camera" ? this._createCameraSectionContent() : "";

            return `<section class="threeDDebugPanel__section${activeClass}" data-debug-section="${section.id}">${sectionContent}</section>`;
        }).join("");

        return `
            <div class="threeDDebugPanel tabs" id="${this.idPrefix}">
                <ul>${tabs}</ul>
                <div class="tab-content">${content}</div>
            </div>
        `;
    }

    /**
     * Binds click handlers that switch the active debug tab.
     *
     * @returns {void}
     */
    _bindTabs() {
        this.container.querySelectorAll("[data-debug-tab]").forEach((button) => {
            button.addEventListener("click", () => this._selectSection(button.dataset.debugTab));
        });
    }

    /**
     * Binds camera copy and restore controls inside the camera debug tab.
     *
     * @returns {void}
     */
    _bindCameraControls() {
        this.cameraInput = this.container.querySelector("[data-camera-uri-input]");
        this.cameraStatus = this.container.querySelector("[data-camera-status]");

        this.container.querySelector("[data-camera-copy]")?.addEventListener("click", () => this._copyCameraUri());
        this.container.querySelector("[data-camera-apply]")?.addEventListener("click", () => this._applyCameraUri());
        this.cameraInput?.addEventListener("keydown", (event) => {
            if (event.key === "Enter") {
                this._applyCameraUri();
            }
        });
    }

    /**
     * Connects each collector section to its matching DOM section.
     *
     * @returns {void}
     */
    _bindStatContainers() {
        this.collector.getSections().forEach((section) => {
            if (section.id === "camera") {
                return;
            }

            this.collector.setDisplayContainer(
                section.id,
                this.container.querySelector(`[data-debug-section="${section.id}"]`)
            );
        });
    }

    /**
     * Selects one visible tab and hides all other debug sections.
     *
     * @param {string} sectionId Section identifier to show.
     * @returns {void}
     */
    _selectSection(sectionId) {
        this.activeSectionId = sectionId;
        this.container.querySelectorAll("[data-debug-tab]").forEach((button) => {
            button.classList.toggle("selectedItem", button.dataset.debugTab === sectionId);
        });
        this.container.querySelectorAll("[data-debug-section]").forEach((section) => {
            section.classList.toggle("selectedItem", section.dataset.debugSection === sectionId);
        });
        this.refresh();
    }

    /**
     * Creates static controls for reading, copying, and restoring camera state.
     *
     * @returns {string} HTML string for the camera debug tab.
     */
    _createCameraSectionContent() {
        return `
            <div class="threeDDebugPanel__camera">
                <div class="threeDDebugPanel__cameraInfo" data-camera-info></div>
                <div class="threeDDebugPanel__cameraControls">
                    <input type="text" class="threeDDebugPanel__cameraInput" data-camera-uri-input aria-label="Camera URI parameters">
                    <button type="button" class="threeDDebugPanel__cameraButton" data-camera-copy>Copy</button>
                    <button type="button" class="threeDDebugPanel__cameraButton" data-camera-apply>Apply</button>
                </div>
                <div class="threeDDebugPanel__cameraStatus" data-camera-status></div>
            </div>
        `;
    }

    /**
     * Updates visible camera fields and the serialized parameter string.
     *
     * @returns {void}
     */
    _refreshCameraSection() {
        if (!this.container) {
            return;
        }

        const infoContainer = this.container.querySelector("[data-camera-info]");
        const cameraAdapter = this._getCameraAdapter();
        const cameraInfo = cameraAdapter?.getCameraDebugInfo?.() || null;
        const cameraUri = cameraAdapter?.getCameraUri?.() || "";

        if (infoContainer) {
            infoContainer.innerHTML = this._cameraInfoToHtml(cameraInfo);
        }

        if (this.cameraInput && document.activeElement !== this.cameraInput) {
            this.cameraInput.value = cameraUri;
        }
    }

    /**
     * Copies the current serialized camera state to the clipboard.
     *
     * @returns {Promise<void>} Resolves after the copy attempt completes.
     */
    async _copyCameraUri() {
        const cameraUri = this._getCameraAdapter()?.getCameraUri?.() || "";

        if (!cameraUri) {
            this._setCameraStatus("Camera state is not available", true);
            return;
        }

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(cameraUri);
            } else {
                this._copyCameraUriFallback(cameraUri);
            }
            this._setCameraStatus("Copied");
        } catch (error) {
            this._setCameraStatus(error.message || "Clipboard copy failed", true);
        }
    }

    /**
     * Restores camera state from the current input value.
     *
     * @returns {void}
     */
    _applyCameraUri() {
        const value = this.cameraInput?.value || "";

        try {
            const cameraAdapter = this._getCameraAdapter();

            if (typeof cameraAdapter?.restoreCameraFromUri !== "function") {
                throw new Error("Camera restore is not available");
            }

            cameraAdapter.restoreCameraFromUri(value);
            this._setCameraStatus("Applied");
            this._refreshCameraSection();
        } catch (error) {
            this._setCameraStatus(error.message || "Invalid camera parameters", true);
        }
    }

    /**
     * Renders camera info as the same compact list used by stat collectors.
     *
     * @param {object|null} cameraInfo Current camera debug info.
     * @returns {string} Escaped HTML string for camera fields.
     */
    _cameraInfoToHtml(cameraInfo) {
        if (!cameraInfo || typeof cameraInfo !== "object") {
            return "<ul><li>Camera state is not available</li></ul>";
        }

        const items = Object.entries(cameraInfo).map(([key, value]) => {
            return `<li style="display: flex;"><div>${escapeHtml(key)}:&nbsp;</div><div>${escapeHtml(formatCameraValue(value))}</div></li>`;
        }).join("");

        return `<ul>${items}</ul>`;
    }

    /**
     * Shows a short camera action status message.
     *
     * @param {string} message Status text.
     * @param {boolean} [isError=false] Whether the message describes an error.
     * @returns {void}
     */
    _setCameraStatus(message, isError = false) {
        if (!this.cameraStatus) {
            return;
        }

        this.cameraStatus.innerText = message;
        this.cameraStatus.classList.toggle("error", isError);
    }

    /**
     * Returns the active viewer adapter registered in the debug collector.
     *
     * @returns {object|null} Viewer camera adapter, or null before the viewer is ready.
     */
    _getCameraAdapter() {
        return this.collector.getCameraAdapter?.() || null;
    }

    /**
     * Copies camera URI text using the legacy selection command when Clipboard API is unavailable.
     *
     * @param {string} cameraUri Serialized camera parameters.
     * @returns {void}
     * @throws {Error} Throws when the browser refuses the fallback copy command.
     */
    _copyCameraUriFallback(cameraUri) {
        if (!this.cameraInput) {
            throw new Error("Clipboard copy is not available");
        }

        const previousValue = this.cameraInput.value;

        this.cameraInput.value = cameraUri;
        this.cameraInput.focus();
        this.cameraInput.select();

        if (!document.execCommand("copy")) {
            this.cameraInput.value = previousValue;
            throw new Error("Clipboard copy failed");
        }
    }
}

/**
 * Formats scalar and nested camera values for display.
 *
 * @param {*} value Camera value.
 * @returns {string} Display string.
 */
function formatCameraValue(value) {
    if (typeof value === "number" && Number.isFinite(value)) {
        return String(Number(value.toFixed(6)));
    }

    if (typeof value === "object" && value !== null) {
        return JSON.stringify(value);
    }

    return String(value);
}

/**
 * Escapes text interpolated into debug-panel HTML.
 *
 * @param {string} value Raw text value.
 * @returns {string} HTML-safe text.
 */
function escapeHtml(value) {
    return String(value)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}
